/**
 * Turning the medication correction form's codes into words (DECISIONS.md #46).
 *
 * Nothing here holds state and nothing renders, matching `registrationDisplay.ts`.
 *
 * **Every code gets a message.** {@link medicationRefusalMessage} is exhaustive over
 * {@link MedicationRefusalCode} by construction, so a code added on the server and typed into
 * `MEDICATION_REFUSAL_CODES` fails the typecheck here until somebody has written the wording. A
 * default branch would be a blank message on a form where a clinician is fixing a dose.
 *
 * **Six of the twenty-three refusals are about a number or a unit**, and they carry the most wording
 * in the catalogue for a reason: `0,25` and `0.25` differ by nothing a tired reader sees and by a
 * factor of ten in a paediatric dose, so each is refused with a sentence that says what to type
 * rather than with *ogiltigt värde*.
 */

import type { TFunction } from 'i18next'

import { formatClinicalDateTime } from '../format/clinicalDate'
import type { MedicationRefusalCode, MedicationRefusalParams } from './medicationApi'

/**
 * The lookup the form needs.
 *
 * **`patient` is in the list on purpose**, for the reason `registrationDisplay.ts` gives: `Dos`,
 * `Adm.väg`, `Dosering` and `Indikation` already exist there as the medication table's column
 * headings, and the box a clinician types a value into must carry the same word the value is read
 * under.
 */
export type MedicationTranslate = TFunction<['common', 'patient', 'medication']>

/** The fields a correction sends, by the name they travel under in a refusal param. */
export const MEDICATION_FIELD_NAMES = [
  'doseAmount',
  'doseUnit',
  'route',
  'frequency',
  'prnMaxDose24h',
  'indication',
] as const

/**
 * A field's label, reused as the name in a "missing" or "too long" message.
 *
 * Four of the six read from `patient`, which is where the table that displays them gets its headings.
 */
export function medicationFieldLabel(field: string, t: MedicationTranslate): string {
  switch (field) {
    case 'doseAmount':
      return t('patient:medication.columnDose')
    case 'doseUnit':
      return t('medication:field.doseUnit')
    case 'route':
      return t('patient:medication.columnRoute')
    case 'frequency':
      return t('patient:medication.columnSchedule')
    case 'prnMaxDose24h':
      return t('medication:field.prnMaxDose24h')
    case 'indication':
      return t('patient:medication.columnIndication')
    case 'endAt':
      return t('medication:field.endAt')
    case 'status':
      return t('medication:field.status')
    default:
      // A server field this interface has not met. Named raw rather than dropped: the message's job
      // is to say which box to fix.
      return field
  }
}

/**
 * A status in words, for the form's select.
 *
 * `stopped` and `entered-in-error` both take the order off the panel and they are not the same
 * statement — one was given and then stopped, the other was never true. The wording keeps them
 * apart, because a clinician reading the history later needs to know which happened.
 */
export function medicationStatusLabel(status: string, t: MedicationTranslate): string {
  switch (status) {
    case 'active':
      return t('medication:status.active')
    case 'completed':
      return t('medication:status.completed')
    case 'stopped':
      return t('medication:status.stopped')
    case 'entered-in-error':
      return t('medication:status.enteredInError')
    default:
      // The vocabulary is closed by migration 0006's CHECK, so this is unreachable from the form's
      // own list. Showing the code beats hiding the option.
      return status
  }
}

/**
 * What to tell the clinician about a refusal.
 *
 * The `UNKNOWN` case is what a proxy error page or a future code reaches, and its message says the
 * correction was **not** saved — the one fact that matters and is true of every refusal.
 */
export function medicationRefusalMessage(
  code: MedicationRefusalCode | 'UNKNOWN',
  params: MedicationRefusalParams,
  t: MedicationTranslate,
): string {
  switch (code) {
    case 'MEDICATION_DOSE_MISSING':
      return t('medication:refusal.doseMissing')

    case 'MEDICATION_DOSE_COMMA':
      // Names the key to press. Never repaired: a client that turned `0,25` into `0.25` would be
      // deciding what the clinician meant about a number where being wrong is a factor of ten
      // (AGENTS.md §5).
      return t('medication:refusal.doseComma')

    case 'MEDICATION_DOSE_MALFORMED':
      return t('medication:refusal.doseMalformed')

    case 'MEDICATION_DOSE_NOT_POSITIVE':
      return t('medication:refusal.doseNotPositive')

    case 'MEDICATION_UNIT_MISSING':
      return t('medication:refusal.unitMissing')

    case 'MEDICATION_UNIT_NOT_METRIC':
      return t('medication:refusal.unitNotMetric')

    case 'MEDICATION_UNIT_MICROGRAM_ABBREVIATED':
      // The classic thousand-fold error. The message spells the word it wants, because that is the
      // whole remedy: `mikrogram` is long precisely so it cannot be read as `mg`.
      return t('medication:refusal.unitMicrogramAbbreviated')

    case 'MEDICATION_STATUS_INVALID':
      return t('medication:refusal.statusInvalid')

    case 'MEDICATION_FIELD_MISSING':
      return t('medication:refusal.fieldMissing', {
        field: medicationFieldLabel(params.field ?? '', t),
      })

    case 'MEDICATION_FIELD_TOO_LONG':
      return t('medication:refusal.fieldTooLong', {
        // The server's limit, not a client constant, so a drift between the two reads as a
        // disagreement somebody can see rather than as a wrong number in a message.
        field: medicationFieldLabel(params.field ?? '', t),
        maximum: params.max ?? 0,
      })

    case 'MEDICATION_IS_PRN_MISSING':
      return t('medication:refusal.isPrnMissing')

    case 'MEDICATION_PRN_MAX_DOSE_MISSING':
      // A vid behov order with no ceiling. Refused rather than left blank, and never computed from
      // the dose and the frequency — that computation is dose calculation (AGENTS.md §3 rule 5).
      return t('medication:refusal.prnMaxDoseMissing')

    case 'MEDICATION_END_AT_MALFORMED':
      return t('medication:refusal.endAtMalformed')

    case 'MEDICATION_END_BEFORE_START':
      // The one refusal whose param is displayed. `start_at` is inherited from the version being
      // corrected, so the form never held it and cannot have shown it — quoting it back is what
      // makes this actionable instead of puzzling. Through the shared date formatter, like every
      // other clinical timestamp: `YYYY-MM-DD HH:mm`, never a locale-dependent `Date` method
      // (AGENTS.md §3 rule 7).
      return t('medication:refusal.endBeforeStart', {
        start: params.startAt ? formatClinicalDateTime(params.startAt) : '',
      })

    case 'MEDICATION_NOT_FOUND':
      return t('medication:refusal.notFound')

    case 'MEDICATION_RETRACTED':
      return t('medication:refusal.retracted')

    case 'MEDICATION_VERSION_STALE':
      return t('medication:refusal.versionStale')

    case 'MEDICATION_NO_CHANGE':
      // Reached by resending the same dose written at another scale, among other things: `0.250` and
      // `0.25` are the same number, so the record gains nothing by holding a second version of it.
      return t('medication:refusal.noChange')

    case 'MEDICATION_SUPERSEDES_MISSING':
      return t('medication:refusal.supersedesMissing')

    case 'PATIENT_NOT_FOUND':
      return t('medication:refusal.patientNotFound')

    case 'PATIENT_RETRACTED':
      return t('medication:refusal.patientRetracted')

    case 'ROLE_MAY_NOT_DOCUMENT':
      return t('medication:refusal.roleMayNotDocument')

    case 'MEDICATION_BODY_MISSING':
    case 'UNKNOWN':
      return t('medication:refusal.unknown')
  }
}

/**
 * What to tell the clinician when the correction could not be attempted, or answered something
 * unusable.
 *
 * Separate wording from every refusal, because the action is opposite: a refusal is something to fix
 * in the form, and this is something to wait out — so the message says to leave the form open.
 */
export function medicationUnavailableMessage(t: MedicationTranslate): string {
  return t('medication:refusal.unavailable')
}
